import { seoLinks, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { BlogList } from "@/components/site/BlogViews";
import { CtaBand, PageHero } from "@/components/site/Page";
import { InstagramFollowCta } from "@/components/site/Instagram";
import { listPublishedPosts } from "@/lib/blog.functions";
import { getInstagramFeed } from "@/lib/instagram.functions";
import { safeLoader } from "@/lib/safe-loader";

const TITLE = "Blog – Colegio sueco en España | SIS Estepona";
const DESCRIPTION =
  "Apuntes del día a día del colegio sueco entre Estepona y Marbella: vida escolar, enseñanza, deporte y consejos para familias que se mudan a la Costa del Sol.";

export const Route = createFileRoute("/es/blog/")({
  loader: async ({ context }) => {
    const [posts, instagram] = await Promise.all([
      safeLoader("es.blog.posts", () => context.queryClient.ensureQueryData({
        queryKey: ["blog-list", "es"],
        queryFn: () => listPublishedPosts({ data: { locale: "es", limit: 24 } }),
      }), []),
      safeLoader("es.blog.instagram", () => context.queryClient.ensureQueryData({
        queryKey: ["instagram-feed"],
        queryFn: () => getInstagramFeed(),
      }), { posts: [], highlights: [] }),
    ]);
    return { posts, instagramPosts: instagram.posts };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/blog"),
  }),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      El blog no se ha podido cargar ahora mismo. Inténtalo de nuevo en unos minutos.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">Página no encontrada.</div>
  ),
  component: BlogIndex,
});

function BlogIndex() {
  const { posts, instagramPosts } = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Blog"
        title="Desde el día a día del colegio"
        intro="Aquí escribimos sobre la enseñanza, la vida escolar y la vida en la Costa del Sol: los textos que nos habría gustado leer antes de nuestra propia mudanza."
      />

      <BlogList posts={posts} locale="es" />

      <div className="py-10 sm:py-14">
        <InstagramFollowCta posts={instagramPosts} locale="es" />
      </div>

      <CtaBand locale="es" />
    </>
  );
}
